import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Award, Flame } from "lucide-react";

const DOT = { on_time: "#059669", late: "#F59E0B", missing: "#E2E8F0", pending: "#BFDBFE" };
const TITLE = { on_time: "On time", late: "Uploaded late", missing: "Not uploaded", pending: "Due soon" };

export default function UploadStreak({ refreshKey = 0 }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/statement-requests/my-streak").then((r) => setS(r.data)).catch(() => {}); }, [refreshKey]);
  if (!s || !s.has_uploads) return null;
  const msg = s.streak >= 12 ? "A full year of on-time statements. Your books are in great shape, thank you!"
    : s.streak >= 3 ? `Thank you! ${s.streak} months in a row on time keeps your books complete and your reports accurate.`
      : s.streak > 0 ? "Thanks for uploading on time. Keep it going to build your streak."
        : "Upload each month's statement by the end of the next month to start a streak.";
  return (
    <section className="bg-white border p-5 mb-6" style={{ borderColor: "var(--ll-border)" }} data-testid="client-upload-streak">
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-14 h-14 grid place-items-center" style={{ background: s.streak ? "#FFF7ED" : "var(--ll-mint)" }}>
          <Flame size={26} className={s.streak ? "text-[#EA580C]" : "text-[var(--ll-ink-muted)]"} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-xl" data-testid="client-streak-count">
            {s.streak} month{s.streak === 1 ? "" : "s"} on time
          </div>
          <div className="text-sm text-[var(--ll-ink-soft)]" data-testid="client-streak-message">{msg}</div>
          {s.pending && <div className="text-xs mt-1 text-[#1D4ED8]" data-testid="client-streak-pending">{s.pending.label} is due by {fmtDate(s.pending.due)} to keep your streak.</div>}
        </div>
        <div className="flex gap-2" data-testid="client-streak-badges">
          {s.milestones.map((m) => (
            <span key={m.months} title={m.earned ? "Earned" : `${m.months} months in a row`} data-testid={`client-streak-badge-${m.months}`} data-earned={m.earned}
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 inline-flex items-center gap-1 border"
              style={m.earned ? { background: "#EDE9FE", color: "#5B21B6", borderColor: "#C4B5FD" } : { color: "#94A3B8", borderColor: "var(--ll-border)" }}>
              <Award size={11} /> {m.months} mo
            </span>
          ))}
        </div>
      </div>
      <div className="flex gap-1 mt-4" data-testid="client-streak-months">
        {s.months.map((m) => (
          <div key={m.month} className="flex-1 h-2" style={{ background: DOT[m.status] }} title={`${m.label}: ${TITLE[m.status]}`} />
        ))}
      </div>
      <div className="flex justify-between gap-2 text-[10px] text-[var(--ll-ink-muted)] mt-1">
        <span className="hidden sm:inline">{s.months[0]?.label}</span>
        <span className="text-center flex-1 sm:flex-none">{s.next_milestone ? `${s.to_next} more to the ${s.next_milestone}-month badge · best ${s.best}` : `Best streak ${s.best}`}</span>
        <span className="hidden sm:inline">{s.months[s.months.length - 1]?.label}</span>
      </div>
    </section>
  );
}
